<template>
  <div>
    <el-dialog
      :title="displayData.title"
      :visible="true"
      width="650px"
      :close-on-click-modal="false"
      @close="$emit('close')"
    >
      <BaseElForm ref="formRef" :model="formData" :rules="formRules" label-position="top">
        <BaseElFormItem prop="timeUnit">
          <template slot="label">
            <FormItemTooltipLabel :label="displayData.timeUnit.label">
              設定客人每隔多久可以預約一次，例如設定 30 分鐘，則可預約的時間點將會是 00:00、00:30、01:00... 等
            </FormItemTooltipLabel>
          </template>
          <div>
            <FlexLayout :gap="8" align="center">
              <div
                v-for="item in displayQuickTimeUnitButtons"
                :key="item.value"
                @click="onQuickSetTimeUnit(item)"
              >
                <Tag :type="quickTimeUnitActive(item) ? 'action' : 'info'">{{ item.label }}</Tag>
              </div>
            </FlexLayout>
            <ElInputWrapper>
              <FlexLayout :gap="8" align="center" class="text-gray-100">
                每
                <BaseElInput
                  v-model="formData.timeUnit"
                  testName="formData_timeUnit"
                  style="width: 78px;"
                />
                分鐘 為單位設立一個預約時段
              </FlexLayout>
            </ElInputWrapper>
          </div>
        </BaseElFormItem>

        <BaseElFormItem prop="timeUnitOffset">
          <template slot="label">
            <FormItemTooltipLabel :label="displayData.timeUnitOffset.label">
              微調預約時間點，例如原本時間點是整點 (00:00)，但想讓客人從 00:15 開始預約，就設定 15 分鐘
            </FormItemTooltipLabel>
          </template>
          <div>
            <FlexLayout :gap="8" align="center">
              <div
                v-for="item in displayQuickTimeOffsetButtons"
                :key="item.value"
                @click="onQuickSetTimeOffset(item)"
              >
                <Tag :type="quickTimeUnitOffsetActive(item) ? 'action' : 'info'">{{ item.label }}</Tag>
              </div>
            </FlexLayout>
            <ElInputWrapper>
              <FlexLayout :gap="8" align="center" class="text-gray-100">
                時間往後調整
                <BaseElInput
                  v-model="formData.timeUnitOffset"
                  testName="formData_timeUnitOffset"
                  style="width: 78px;"
                />
                分鐘
              </FlexLayout>
            </ElInputWrapper>
          </div>
        </BaseElFormItem>

        <PeriodOffsetPreviewBlock />
      </BaseElForm>

      <div slot="footer">
        <BaseElButton plain :loading="loading" @click="$emit('close')">{{ displayData.btn.cancel }}</BaseElButton>
        <BaseElButton type="primary" data-cy="update-time-parameter" @click="confirmSubmit"> {{ displayData.btn.save }} </BaseElButton>
      </div>
    </el-dialog>
    <BaseDialog
      v-if="modal.warning"
      width="566px"
      :title="displayData.dialog.title"
      :btn2Loading="loading"
      @confirm="onSubmit"
      @cancel="modal.warning = false"
      @close="modal.warning = false"
    >
      <div class="dialog-content">
        <p class="mb-[20px]">{{ displayData.dialog.content }}</p>
      </div>
    </BaseDialog>
  </div>
</template>

<script>
import { UpdateReservationParameter } from '@/api/reservation'
import { useBaseForm } from '@/use/useForm'
import { computed, defineComponent, onBeforeMount, provide, reactive } from 'vue'
import { useShop } from '@/use/shop'
import notifyMessage from '@/config/notifyMessage'
import { isDigitRules, noEmptyRules, rangeRules } from '@/validation'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import { i18n } from '@/plugins/i18n/i18n'
import FormItemTooltipLabel from '@/components/Form/FormItemTooltipLabel.vue'
import { includes, toNumber } from 'lodash'
import PeriodOffsetPreviewBlock from './components/PeriodOffsetPreviewBlock.vue'

export default defineComponent({
  name: 'EditReservationBasicSettingsModal',
  components: {
    BaseDialog,
    FormItemTooltipLabel,
    PeriodOffsetPreviewBlock,
  },
  props: {
    configData: { type: Object, default: () => ({}) },
  },
  setup (props, { emit }) {
    const { formRef, initFormData, formData, checkForm, loading } = useBaseForm()
    const { shopId } = useShop()
    const modal = reactive({
      warning: false,
    })

    const formRules = computed(() => {
      return {
        timeUnit: [isDigitRules(), noEmptyRules(), rangeRules(10)],
        timeUnitOffset: [noEmptyRules(), isDigitRules(), rangeRules()],
      }
    })
    const displayData = computed(() => {
      const data = {
        title: i18n.t('reservationBasicSettings.dialog.title'),
        timeUnit: {
          label: i18n.t('reservationBasicSettings.timeUnit.title'),
        },
        timeUnitOffset: {
          label: i18n.t('reservationBasicSettings.timeUnitOffset.title'),
        },
        btn: {
          cancel: i18n.t('common.button.cancel.text'),
          save: i18n.t('common.button.save.text'),
        },
        dialog: {
          title: i18n.t('reservationBasicSettings.dialog.warning.title'),
          content: i18n.t('reservationBasicSettings.dialog.warning.content'),
        },
      }
      return data
    })

    const displayQuickTimeUnitButtons = computed(() => {
      return [
        { label: '15 分鐘', value: 15 },
        { label: '30 分鐘', value: 30 },
        { label: '1 小時', value: 60 },
        { label: '1.5 小時', value: 90 },
        { label: '2 小時', value: 120 },
        { label: '自定間隔', value: 'custom' },
      ]
    })

    const displayQuickTimeOffsetButtons = computed(() => {
      return [
        { label: '不調整', value: 0 },
        { label: '+5 分鐘', value: 5 },
        { label: '+10 分鐘', value: 10 },
        { label: '+15 分鐘', value: 15 },
        { label: '+30 分鐘', value: 30 },
        { label: '自定時間', value: 'custom' },
      ]
    })

    const onSubmit = async () => {
      if (loading.value) return
      loading.value = true
      if (!(await checkForm(formRef.value))) return
      const [, err] = await UpdateReservationParameter({
        shopId: shopId.value,
        ...props.configData,
        timeUnit: formData.timeUnit,
        timeUnitOffset: formData.timeUnitOffset,
        enableClientCustomTimeSlots: false, // Resetting this field
        clientCustomTimeSlots: [], // Resetting this field
      })
      loading.value = false
      if (err) {
        window.$message.error(err)
        return
      }

      window.$message.success(notifyMessage.updateSuccess)
      emit('close')
      emit('refresh')
    }

    const confirmSubmit = async () => {
      if (loading.value) return
      loading.value = true
      if (!(await checkForm(formRef.value))) {
        loading.value = false
        return
      }
      modal.warning = true
      loading.value = false
    }

    const quickTimeUnitActive = (item) => {
      if (item.value === 'custom') {
        if (!includes([
          15, 30, 60, 90, 120,
        ], toNumber(formData.timeUnit))) return true
      }
      return item.value === toNumber(formData.timeUnit)
    }

    const quickTimeUnitOffsetActive = (item) => {
      if (item.value === 'custom') {
        if (!includes([0, 5, 10, 15, 30], toNumber(formData.timeUnitOffset))) return true
      }
      return item.value === toNumber(formData.timeUnitOffset)
    }

    const onQuickSetTimeUnit = (item) => {
      if (item.value === 'custom') return
      formData.timeUnit = item.value
    }

    const onQuickSetTimeOffset = (item) => {
      if (item.value === 'custom') return
      formData.timeUnitOffset = item.value
    }

    provide('formData', formData)

    onBeforeMount(() => {
      initFormData({
        timeUnit: props.configData.timeUnit,
        timeUnitOffset: props.configData.timeUnitOffset,
      })
    })

    return {
      formRef,
      onSubmit,
      confirmSubmit,
      loading,
      formRules,
      formData,
      modal,
      displayData,
      displayQuickTimeUnitButtons,
      displayQuickTimeOffsetButtons,
      onQuickSetTimeUnit,
      onQuickSetTimeOffset,
      quickTimeUnitActive,
      quickTimeUnitOffsetActive,
    }
  },
})
</script>

<style lang="postcss" scoped></style>
